// src/pages/MyPass.jsx
// Piano With Pan — My Pass Details Page
//
// Shows current premium pass details: plan, activated date, expiry, days remaining.
// Protected route — requires login + active pass (redirect to paywall if not premium).

import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePremiumStatus } from "../hooks/usePremiumStatus";

const PLAN_LABELS = {
  daily: "Day Pass",
  weekly: "Weekly Pass",
  monthly: "Monthly Pass",
};

const PLAN_PRICES = {
  daily: 49,
  weekly: 99,
  monthly: 299,
};

function formatDate(isoString) {
  if (!isoString) return "—";
  try {
    return new Date(isoString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function getDaysRemaining(expiresAt) {
  if (!expiresAt) return 0;
  try {
    const expMs = new Date(expiresAt).getTime();
    const nowMs = Date.now();
    const diffMs = expMs - nowMs;
    return Math.max(0, Math.ceil(diffMs / (24 * 60 * 60 * 1000)));
  } catch {
    return 0;
  }
}

function getProgressPercent(startAt, expiresAt) {
  if (!startAt || !expiresAt) return 0;
  try {
    const start = new Date(startAt).getTime();
    const end = new Date(expiresAt).getTime();
    const now = Date.now();
    const total = end - start;
    const elapsed = now - start;
    return Math.min(100, Math.max(0, (elapsed / total) * 100));
  } catch {
    return 0;
  }
}

export default function MyPass() {
  const navigate = useNavigate();
  const { isPremium, premiumLoaded, pass } = usePremiumStatus();

  useEffect(() => {
    // If premium check completes and user is not premium, redirect to paywall
    if (premiumLoaded && !isPremium) {
      navigate("/pwp-paywall?reason=premium_required", { replace: true });
    }
  }, [premiumLoaded, isPremium, navigate]);

  // Show loading while checking
  if (!premiumLoaded) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(180deg, #F0F4FF 0%, #ffffff 60%, #FFF7ED 100%)" }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@700;800;900&display=swap');`}</style>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 48, height: 48, margin: "0 auto 16px", border: "4px solid #e5e7eb", borderTopColor: "#7c3aed", borderRadius: "50%", animation: "spin 0.8s linear infinite" }}></div>
          <div style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 700, color: "#64748b" }}>Checking your pass…</div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); }}`}</style>
      </div>
    );
  }

  // If not premium after check completes, the useEffect above redirects — this is a fallback
  if (!isPremium || !pass) {
    return null;
  }

  const planId = pass.plan_id || "monthly";
  const planLabel = PLAN_LABELS[planId] || "Premium Pass";
  const price = PLAN_PRICES[planId] || 299;
  const startAt = pass.start_at;
  const expiresAt = pass.expires_at;
  const daysLeft = getDaysRemaining(expiresAt);
  const progress = getProgressPercent(startAt, expiresAt);

  return (
    <div className="mp-root">
      <style>{CSS}</style>

      {/* Background */}
      <div className="mp-bg">
        <div className="mp-bg-base" />
        <div className="mp-blob mp-blob-1" />
        <div className="mp-blob mp-blob-2" />
      </div>

      {/* Header */}
      <header className="mp-header">
        <button className="mp-back-btn" onClick={() => navigate("/game-menu")} aria-label="Back to menu">
          ← Back
        </button>
        <h1 className="mp-header-title">My Pass</h1>
        <div style={{ width: 80 }} /> {/* Spacer for centering */}
      </header>

      <main className="mp-main">
        <div className="mp-card">
          {/* Status badge */}
          <div className="mp-status-badge">
            <span className="mp-status-icon">✓</span>
            <span className="mp-status-text">Active</span>
          </div>

          {/* Plan title */}
          <h2 className="mp-plan-title">{planLabel}</h2>
          <div className="mp-plan-price">₹{price}/month</div>

          {/* Details grid */}
          <div className="mp-details-grid">
            <div className="mp-detail-card">
              <div className="mp-detail-label">Activated</div>
              <div className="mp-detail-value">{formatDate(startAt)}</div>
            </div>

            <div className="mp-detail-card">
              <div className="mp-detail-label">Expires</div>
              <div className="mp-detail-value">{formatDate(expiresAt)}</div>
            </div>

            <div className="mp-detail-card mp-detail-card-highlight">
              <div className="mp-detail-label">Days Remaining</div>
              <div className="mp-detail-value mp-detail-value-large">{daysLeft}</div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mp-progress-section">
            <div className="mp-progress-label">Pass Duration</div>
            <div className="mp-progress-bar">
              <div className="mp-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <div className="mp-progress-caption">
              {Math.round(progress)}% of pass used · {daysLeft} days left
            </div>
          </div>

          {/* What's included */}
          <div className="mp-includes">
            <div className="mp-includes-title">What's Included</div>
            <ul className="mp-includes-list">
              <li>✓ All 10 piano lessons unlocked</li>
              <li>✓ Levels 4–10: Machli Jal Ki Rani, Jingle Bells, Incy Wincy Spider + 4 more</li>
              <li>✓ Complete the Treble Town quest — 8 stars + final boss battle</li>
              <li>✓ Unlimited play on all levels</li>
              <li>✓ Virtual piano + real piano microphone mode</li>
            </ul>
          </div>

          {/* Renewal note */}
          <div className="mp-renewal-note">
            <strong>Note:</strong> After {daysLeft} days, your pass will expire. You can renew anytime to continue playing premium levels. Your progress is always saved.
          </div>

          {/* Action buttons */}
          <div className="mp-actions">
            <button className="mp-btn mp-btn-primary" onClick={() => navigate("/game-menu")}>
              Continue Playing
            </button>
            <button className="mp-btn mp-btn-secondary" onClick={() => navigate("/pwp-paywall")}>
              Renew Early
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@600;700;800;900&display=swap');

.mp-root {
  min-height: 100vh;
  font-family: 'Nunito', sans-serif;
  color: #0f172a;
  position: relative;
}
.mp-root * { box-sizing: border-box; }

/* Background */
.mp-bg {
  pointer-events: none;
  position: fixed;
  inset: 0;
  z-index: 0;
  overflow: hidden;
}
.mp-bg-base {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, #F0F4FF 0%, #ffffff 60%, #FFF7ED 100%);
}
.mp-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.3;
  animation: mpFloat 20s infinite ease-in-out;
}
.mp-blob-1 {
  top: -5%;
  left: -8%;
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #fde68a, #fbbf24);
}
.mp-blob-2 {
  bottom: -10%;
  right: -5%;
  width: 360px;
  height: 360px;
  background: linear-gradient(135deg, #c4b5fd, #8b5cf6);
  animation-delay: 4s;
}
@keyframes mpFloat {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(15px, -20px) scale(1.04); }
}

/* Header */
.mp-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(14px);
  border-bottom: 1px solid #f1f5f9;
}
.mp-back-btn {
  padding: 8px 14px;
  border-radius: 10px;
  border: 2px solid #e2e8f0;
  background: #fff;
  font-family: 'Nunito', sans-serif;
  font-size: 0.9rem;
  font-weight: 700;
  color: #0f172a;
  cursor: pointer;
  transition: all 0.15s;
}
.mp-back-btn:hover {
  border-color: #cbd5e1;
  transform: translateY(-1px);
}
.mp-header-title {
  font-family: 'Fredoka One', cursive;
  font-size: 1.3rem;
  color: #7c3aed;
  margin: 0;
}

/* Main */
.mp-main {
  position: relative;
  z-index: 1;
  padding: 24px 20px 60px;
  max-width: 560px;
  margin: 0 auto;
}

/* Card */
.mp-card {
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 24px;
  padding: 32px 24px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
}

/* Status badge */
.mp-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 99px;
  background: linear-gradient(135deg, #d1fae5, #ecfdf5);
  border: 1.5px solid #86efac;
  margin-bottom: 20px;
}
.mp-status-icon {
  font-size: 0.9rem;
  color: #16a34a;
}
.mp-status-text {
  font-family: 'Fredoka One', cursive;
  font-size: 0.8rem;
  color: #15803d;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Plan title */
.mp-plan-title {
  font-family: 'Fredoka One', cursive;
  font-size: 1.8rem;
  color: #0f172a;
  margin: 0 0 6px;
}
.mp-plan-price {
  font-size: 1rem;
  font-weight: 700;
  color: #7c3aed;
  margin-bottom: 24px;
}

/* Details grid */
.mp-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 24px;
}
.mp-detail-card {
  padding: 16px;
  border-radius: 14px;
  background: linear-gradient(135deg, #f8fafc, #ffffff);
  border: 1.5px solid #e2e8f0;
}
.mp-detail-card-highlight {
  grid-column: 1 / -1;
  background: linear-gradient(135deg, #fef3c7, #fffbeb);
  border-color: #fbbf24;
}
.mp-detail-label {
  font-size: 0.7rem;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}
.mp-detail-value {
  font-family: 'Fredoka One', cursive;
  font-size: 1.1rem;
  color: #0f172a;
}
.mp-detail-value-large {
  font-size: 2rem;
  color: #d97706;
}

/* Progress bar */
.mp-progress-section {
  margin-bottom: 24px;
}
.mp-progress-label {
  font-size: 0.75rem;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
}
.mp-progress-bar {
  height: 10px;
  border-radius: 99px;
  background: #e5e7eb;
  overflow: hidden;
  margin-bottom: 8px;
}
.mp-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #7c3aed, #a78bfa);
  border-radius: 99px;
  transition: width 0.4s ease;
}
.mp-progress-caption {
  font-size: 0.8rem;
  font-weight: 700;
  color: #94a3b8;
  text-align: center;
}

/* Includes */
.mp-includes {
  margin-bottom: 20px;
  padding: 20px;
  border-radius: 14px;
  background: linear-gradient(135deg, #f5f3ff, #faf5ff);
  border: 1.5px solid #e9d5ff;
}
.mp-includes-title {
  font-family: 'Fredoka One', cursive;
  font-size: 1rem;
  color: #7c3aed;
  margin-bottom: 12px;
}
.mp-includes-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.mp-includes-list li {
  font-size: 0.88rem;
  font-weight: 700;
  color: #475569;
  margin-bottom: 8px;
  line-height: 1.4;
}
.mp-includes-list li:last-child {
  margin-bottom: 0;
}

/* Renewal note */
.mp-renewal-note {
  padding: 16px;
  border-radius: 12px;
  background: #fef2f2;
  border: 1.5px solid #fca5a5;
  font-size: 0.85rem;
  font-weight: 700;
  color: #991b1b;
  line-height: 1.5;
  margin-bottom: 24px;
}

/* Actions */
.mp-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.mp-btn {
  padding: 14px 20px;
  border-radius: 12px;
  font-family: 'Fredoka One', cursive;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  width: 100%;
  text-align: center;
}
.mp-btn-primary {
  background: linear-gradient(135deg, #7c3aed, #6d28d9);
  color: #fff;
  box-shadow: 0 4px 0 #5b21b6, 0 8px 20px rgba(124, 58, 237, 0.18);
}
.mp-btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 0 #5b21b6, 0 12px 28px rgba(124, 58, 237, 0.25);
}
.mp-btn-primary:active {
  transform: translateY(1px);
  box-shadow: 0 2px 0 #5b21b6;
}
.mp-btn-secondary {
  background: #fff;
  color: #7c3aed;
  border: 2px solid #c4b5fd;
}
.mp-btn-secondary:hover {
  border-color: #a78bfa;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.1);
}

@media (max-width: 480px) {
  .mp-card {
    padding: 24px 20px;
  }
  .mp-plan-title {
    font-size: 1.5rem;
  }
  .mp-details-grid {
    grid-template-columns: 1fr;
  }
  .mp-detail-card-highlight {
    grid-column: 1;
  }
}
`;